import { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  RefreshCw,
  Download,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  Users,
} from 'lucide-react';
import { financialReportService } from '../services/financialReportService';
import { downloadFinancialCSV } from '../services/financialExportService';
import { SummaryCards } from './financial-report/SummaryCards';
import { StatusBreakdown } from './financial-report/StatusBreakdown';
import { FinancialTable } from './financial-report/FinancialTable';
import { AgentBreakdown } from './financial-report/AgentBreakdown';
import { BillingAlerts } from './financial-report/BillingAlerts';
import { log } from '../lib/logger';
import { REPORT_MODE_LABELS } from '../types/financialReport';
import type {
  ProcessedEntry,
  FinancialSummary,
  AgentSummary,
  InfluencerSummary,
  ReportTab,
} from '../types/financialReport';

export function FinancialReport() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allEntries, setEntries] = useState<ProcessedEntry[]>([]);
  const [modeFilter, setModeFilter] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');

  const loadData = async (showFullLoading = true) => {
    if (showFullLoading) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await financialReportService.getAll(selectedYear);
      setEntries(data);
    } catch (error) {
      log.error('Error loading financial report:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const modeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of allEntries) counts.set(entry.campaign_mode, (counts.get(entry.campaign_mode) ?? 0) + 1);
    return counts;
  }, [allEntries]);

  // The filter applies to everything on the page, the export included
  const entries = useMemo(
    () => (modeFilter === 'all' ? allEntries : allEntries.filter(e => e.campaign_mode === modeFilter)),
    [allEntries, modeFilter]
  );

  const summary: FinancialSummary = useMemo(
    () => financialReportService.calculateSummary(entries),
    [entries]
  );

  const agents: AgentSummary[] = useMemo(
    () => financialReportService.calculateAgentSummaries(entries),
    [entries]
  );

  const influencers: InfluencerSummary[] = useMemo(
    () => financialReportService.calculateInfluencerSummaries(entries),
    [entries]
  );

  const receivables = useMemo(
    () => financialReportService.getReceivables(entries),
    [entries]
  );

  const payables = useMemo(
    () => financialReportService.getPayables(entries),
    [entries]
  );

  const pipeline = useMemo(
    () => financialReportService.getPipeline(entries),
    [entries]
  );

  const billingAlerts = useMemo(
    () => financialReportService.getBillingAttention(entries),
    [entries]
  );

  const handleExportCSV = () => {
    downloadFinancialCSV(entries, [selectedYear || 'all', modeFilter === 'all' ? '' : modeFilter].filter(Boolean).join('_'));
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: undefined, label: 'Todos os anos' },
    { value: currentYear, label: String(currentYear) },
    { value: currentYear - 1, label: String(currentYear - 1) },
  ];

  const tabs: { key: ReportTab; label: string; icon: typeof BarChart3; count?: number }[] = [
    { key: 'overview', label: 'Visão geral', icon: BarChart3 },
    { key: 'receivables', label: 'A receber', icon: ArrowDownRight, count: receivables.length },
    { key: 'payables', label: 'A pagar', icon: ArrowUpRight, count: payables.length },
    { key: 'pipeline', label: 'Pipeline', icon: Briefcase, count: pipeline.length },
    { key: 'agents', label: 'Equipa e influencers', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-xl p-5 md:p-6 shadow-sm border border-gray-100 gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-3.5 shadow-lg shadow-blue-500/20">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Relatório Financeiro</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Todos os tipos de campanha e projectos: a receber, a pagar e em perspetiva
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
              className="bg-white border border-gray-200 text-gray-900 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 text-sm font-medium shadow-sm"
            >
              {yearOptions.map((opt) => (
                <option key={opt.label} value={opt.value || ''}>{opt.label}</option>
              ))}
            </select>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 rounded-full px-4 py-2.5 hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar CSV</span>
            </button>

            <button
              onClick={() => loadData(false)}
              disabled={refreshing}
              className="flex items-center gap-2 bg-accent-600 hover:bg-accent-700 text-white rounded-full px-5 py-2.5 transition-all disabled:opacity-50 text-sm font-semibold shadow-sm hover:shadow-md"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por tipo">
          {[
            { key: 'all', label: 'Todos', count: allEntries.length },
            ...Object.entries(REPORT_MODE_LABELS).map(([key, label]) => ({
              key,
              label: key === 'projecto' ? 'Projectos' : key === 'sponsorship' ? 'Patrocínios' : label,
              count: modeCounts.get(key) ?? 0,
            })),
          ].map(option => (
            <button
              key={option.key}
              type="button"
              aria-pressed={modeFilter === option.key}
              onClick={() => setModeFilter(option.key)}
              className={`h-8 px-3 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                modeFilter === option.key ? 'bg-brand-ink text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:ring-gray-400'
              }`}
            >
              {option.label} <span className="tabular-nums opacity-70">{option.count}</span>
            </button>
          ))}
        </div>

        <SummaryCards summary={summary} loading={loading} />

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 border border-gray-100">
          <div className="flex items-center gap-1 p-2 overflow-x-auto border-b border-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all text-sm font-medium whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-green-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                      activeTab === tab.key ? 'bg-white/20' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <StatusBreakdown summary={summary} loading={loading} />
            <BillingAlerts entries={billingAlerts} loading={loading} />
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-4">
                <CampaignStatusGrid summary={summary} loading={loading} />
              </div>
            </div>
            <FinancialTable
              entries={entries}
              title="Todas as campanhas e projectos"
              subtitle="Visão financeira completa"
              loading={loading}
            />
          </div>
        )}

        {activeTab === 'receivables' && (
          <FinancialTable
            entries={receivables}
            title="A receber de clientes"
            subtitle="Com faturas ao cliente"
            showInfluencerInvoice={false}
            loading={loading}
          />
        )}

        {activeTab === 'payables' && (
          <FinancialTable
            entries={payables}
            title="A pagar a influencers e fornecedores"
            subtitle="Com custos ou faturas a pagar"
            showClientInvoice={false}
            loading={loading}
          />
        )}

        {activeTab === 'pipeline' && (
          <FinancialTable
            entries={pipeline}
            title="Pipeline: receita futura"
            subtitle="Em consulta, ainda por faturar"
            showClientInvoice={false}
            showInfluencerInvoice={false}
            loading={loading}
          />
        )}

        {activeTab === 'agents' && (
          <AgentBreakdown agents={agents} influencers={influencers} loading={loading} />
        )}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-4">
          <p className="text-xs text-gray-400 text-center">
            Relatório gerado a {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}.
            Valores em euros, sem IVA.
          </p>
        </div>
      </div>
    </div>
  );
}

function CampaignStatusGrid({ summary, loading }: { summary: FinancialSummary; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="h-8 w-12 bg-gray-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  const total = summary.campaigns_completed + summary.campaigns_billing + summary.campaigns_in_progress + summary.campaigns_consultation;

  const statuses = [
    { label: 'Concluídas', count: summary.campaigns_completed, color: 'emerald', pct: total ? (summary.campaigns_completed / total * 100) : 0 },
    { label: 'Em faturação', count: summary.campaigns_billing, color: 'amber', pct: total ? (summary.campaigns_billing / total * 100) : 0 },
    { label: 'Em progresso', count: summary.campaigns_in_progress, color: 'blue', pct: total ? (summary.campaigns_in_progress / total * 100) : 0 },
    { label: 'Em consulta', count: summary.campaigns_consultation, color: 'gray', pct: total ? (summary.campaigns_consultation / total * 100) : 0 },
  ];

  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    gray: 'bg-gray-400',
  };

  const textColorMap: Record<string, string> = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    blue: 'text-blue-700',
    gray: 'text-gray-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Distribuição por estado</h3>
      <div className="h-4 rounded-full bg-gray-100 overflow-hidden flex mb-5">
        {statuses.map((s) => (
          s.pct > 0 ? (
            <div
              key={s.label}
              className={`${colorMap[s.color]} transition-all duration-500`}
              style={{ width: `${s.pct}%` }}
              title={`${s.label}: ${s.count}`}
            />
          ) : null
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statuses.map((s) => (
          <div key={s.label} className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${colorMap[s.color]}`} />
            <div>
              <p className={`text-xl font-bold ${textColorMap[s.color]}`}>{s.count}</p>
              <p className="text-xs text-gray-500">{s.label} ({s.pct.toFixed(0)}%)</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
